// File: src/components/EnhancedPriceRangeVisualizer.tsx

import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { formatCurrency } from "../utils/formatters";
import PriceMarker from "./PriceRangeVisualizer/PriceMarker";

interface EnhancedPriceRangeVisualizerProps {
  min: number;
  max: number;
  current: number;
  entry: number;
  decimals: number; // Número de decimales para formatear cantidades
  /** Formats the prices (bounds, entry, markers) with their unit (defaults to USD). */
  formatPrice?: (value: number) => string;
  /** The line under the nearest bound ("3.1% to floor"); off where the parent already states it. */
  showStatus?: boolean;
}

/** "3.1%"; a tiny non-zero distance reads "<0.1%" rather than "0.0%". */
const formatDistance = (fraction: number): string =>
  fraction > 0 && fraction < 0.0005
    ? "<0.1%"
    : `${(fraction * 100).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export interface RangeStatus {
  text: string;
  /** The bound the sentence refers to; the line sits under that bound's label. */
  side: "floor" | "ceiling";
  outOfRange: boolean;
}

/**
 * Where the current price sits: distance to the nearest bound while in range
 * (relative to the current price), or how far past a bound it is (relative to that bound):
 * "3.1% to floor", "12.4% below floor", "0.8% above ceiling".
 */
export const rangeStatus = (min: number, max: number, current: number): RangeStatus | null => {
  if (!(max > min) || !(current > 0) || !Number.isFinite(current)) return null;
  if (current > max) {
    return { text: `${formatDistance((current - max) / max)} above ceiling`, side: "ceiling", outOfRange: true };
  }
  if (current < min) {
    return { text: `${formatDistance((min - current) / min)} below floor`, side: "floor", outOfRange: true };
  }
  const toCeiling = (max - current) / current;
  const toFloor = (current - min) / current;
  if (toCeiling <= toFloor) {
    return { text: toCeiling === 0 ? "At ceiling" : `${formatDistance(toCeiling)} to ceiling`, side: "ceiling", outOfRange: false };
  }
  return { text: toFloor === 0 ? "At floor" : `${formatDistance(toFloor)} to floor`, side: "floor", outOfRange: false };
};

/** "Floor $2,266.75": secondary label, primary value (the bounds and the entry read the same). */
const PriceCaption: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    {label}{" "}
    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
      {value}
    </Box>
  </>
);

const captionSx = { fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } as const;

/**
 * Bound under the bar: "Floor $2,266.75", plus the status line when it refers to this bound,
 * so a wrapped row never separates the two.
 */
const Bound: React.FC<{ label: string; value: string; status?: RangeStatus | null; align?: "left" | "right" }> = ({
  label,
  value,
  status,
  align = "left",
}) => {
  const theme = useTheme();
  return (
    // Stays right-aligned if a narrow card wraps it onto its own line.
    <Box sx={{ textAlign: align, ml: align === "right" ? "auto" : 0, minWidth: 0 }}>
      <Typography component="div" sx={captionSx}>
        <PriceCaption label={label} value={value} />
      </Typography>
      {status && (
        <Typography
          sx={{
            mt: 0.25,
            fontSize: "0.75rem",
            lineHeight: 1.45,
            fontVariantNumeric: "tabular-nums",
            color: status.outOfRange ? theme.palette.error.main : "text.secondary",
            fontWeight: status.outOfRange ? 600 : 400,
          }}
        >
          {status.text}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Range bar for an LP position: the track is the [min, max] range, with an entry tick and the current
 * price marker (red when it leaves the range). The entry is captioned above its tick ("Entry $2,718.01"),
 * floor / ceiling under the bar's ends; one line under them says how far the current price is from the
 * nearest bound, or how far outside the range it is.
 */
export const EnhancedPriceRangeVisualizer: React.FC<
  EnhancedPriceRangeVisualizerProps
> = ({ min, max, current, entry, decimals, formatPrice, showStatus = true }) => {
  const fmt = formatPrice ?? ((v: number) => formatCurrency(v, decimals));
  const theme = useTheme();
  const range = max - min;

  const getPositionPercent = (value: number): number => {
    if (range <= 0) return 0;
    const pct = ((value - min) / range) * 100;
    return Math.max(0, Math.min(100, pct));
  };

  // No entry tick (or caption) without a usable entry price.
  const hasEntry = Number.isFinite(entry) && entry > 0;
  const entryPos = getPositionPercent(entry);
  const currentPos = getPositionPercent(current);
  const inRange = current >= min && current <= max;
  const status = showStatus ? rangeStatus(min, max, current) : null;

  const markerColor = inRange ? theme.palette.text.primary : theme.palette.error.main;

  return (
    <Box sx={{ width: "100%" }}>
      {hasEntry && (
        // Anchored to the tick: the caption shifts by the tick's own share of its width, so it stays
        // over the tick and never runs past the ends of the bar. The tick carries the same text for
        // screen readers.
        <Box aria-hidden sx={{ position: "relative", height: 18, mb: "2px" }}>
          <Typography
            component="div"
            sx={{
              ...captionSx,
              position: "absolute",
              left: `${entryPos}%`,
              transform: `translateX(-${entryPos}%)`,
              lineHeight: "18px",
            }}
          >
            <PriceCaption label="Entry" value={fmt(entry)} />
          </Typography>
        </Box>
      )}
      <Box sx={{ position: "relative", width: "100%", height: 16 }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            height: 6,
            transform: "translateY(-50%)",
            borderRadius: 3,
            background: `linear-gradient(90deg, ${alpha(theme.palette.secondary.main, 0.18)}, ${alpha(
              theme.palette.secondary.main,
              0.42
            )} 50%, ${alpha(theme.palette.secondary.main, 0.18)})`,
          }}
        />
        {hasEntry && <PriceMarker position={entryPos} valueText={fmt(entry)} label="Entry price" variant="tick" />}
        <PriceMarker
          position={currentPos}
          valueText={fmt(current)}
          label={inRange ? "Current price" : "Current price · out of range"}
          ariaLabel={`Current price ${fmt(current)}${inRange ? "" : ", out of range"}`}
          color={markerColor}
          variant="dot"
        />
      </Box>

      <Box
        sx={{
          mt: 0.75,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-start",
          columnGap: 2,
          rowGap: 0.25,
        }}
      >
        <Bound
          label="Floor"
          value={fmt(min)}
          status={status?.side === "floor" ? status : null}
        />
        <Bound
          label="Ceiling"
          value={fmt(max)}
          status={status?.side === "ceiling" ? status : null}
          align="right"
        />
      </Box>
    </Box>
  );
};

export default EnhancedPriceRangeVisualizer;
